<script setup lang="ts">
import { CONE_RESISTANCE, CRR, CSR, FOS_LIQ, S, LPI, LDI, LSN, PL } from '@/constants/calculationCPTDataPoint'
import type { ReportField } from '@/stores/type/calculationCPT.type'

const store = useCalculationCPTStore()

const { selectedItems, limits, exportReportLoading, exportReportDialog, reportFields } =
	storeToRefs(store)

const REPORT_PARAMS: (ReportField | null)[][] = [
	[CSR, CRR, FOS_LIQ, null],
	[S, CONE_RESISTANCE, null, null],
	[LPI, LDI, LSN, PL]
]

const paramTitles: { [k in ReportField]: string } = {
	CSR: 'Cycle Stress Ratio',
	CRR: 'Cycle Resistance Ratio',
	fos_liq: 'Factor of Safety',
	s: 'Settlement',
	cone_resistance: 'Cone Resistance',
	LPI: 'LPI',
	LDI: 'LDI',
	LSN: 'LSN',
	PL: 'PL',
	LPI_summary: 'LPI Summary',
	LSN_summary: 'LSN Summary'
}

const CRR_limit = computed({
	get: () => limits.value?.CRR,
	set: (value: string) => {
		store.setLimit('CRR', value)
		store.setLimit('CSR', value)
	}
})

const FS_limit = computed({
	get: () => limits.value?.fos_liq,
	set: (value: string) => {
		store.setLimit('fos_liq', value)
	}
})

const getDisabled = (field: ReportField): boolean => {
	return !reportFields.value.includes(field)
}

async function exportReport() {
	await store.exportReport()
	store.toggleExportReportDialog(false)
}
</script>
<template>
	<v-dialog
		:model-value="exportReportDialog"
		@click:outside="store.toggleExportReportDialog()"
		max-width="1500"
	>
		<v-card class="pa-3">
			<v-card-title>Export report</v-card-title>
			<v-card-text class="px-4">
				For each CPT:
				<div class="px-3">
					<v-row no-gutters v-for="row in REPORT_PARAMS">
						<v-col v-for="param in row">
							<v-checkbox
								v-if="param"
								v-model="reportFields"
								:value="param"
								density="compact"
								hide-details
								:label="paramTitles[param]"
								color="primary"
							></v-checkbox>
						</v-col>
					</v-row>
				</div>
				<template v-if="selectedItems.length > 1">
					Summary of CPTs:
					<div class="px-3">
						<v-row no-gutters>
							<v-col>
								<v-checkbox
									v-model="reportFields"
									value="LPI_summary"
									density="compact"
									hide-details
									:label="paramTitles['LPI_summary']"
									color="primary"
								></v-checkbox>
							</v-col>
							<v-col>
								<v-checkbox
									v-model="reportFields"
									value="LSN_summary"
									density="compact"
									hide-details
									:label="paramTitles['LSN_summary']"
									color="primary"
								></v-checkbox>
							</v-col>
							<v-col />
							<v-col />
							<v-col />
						</v-row>
					</div>
				</template>
				Chart upper limits:
				<div class="px-3">
					<v-row no-gutters class="mb-2">
						<v-col class="d-flex align-center">
							<span style="font-size: 14px"> CRR and CSR </span>
						</v-col>
						<v-col>
							<v-text-field
								type="number"
								:disabled="getDisabled('CRR')"
								v-model="CRR_limit"
								hide-details
							></v-text-field>
						</v-col>
						<v-col />
						<v-col />
						<v-col />
					</v-row>
					<v-row no-gutters>
						<v-col class="d-flex align-center">
							<span style="font-size: 14px"> Factor of Safety </span>
						</v-col>
						<v-col>
							<v-text-field
								type="number"
								:disabled="getDisabled('fos_liq')"
								v-model="FS_limit"
								hide-details
							></v-text-field>
						</v-col>
						<v-col />
						<v-col />
						<v-col />
					</v-row>
				</div>
			</v-card-text>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn
					:disabled="exportReportLoading"
					color="primary text-none"
					@click="store.toggleExportReportDialog()"
				>
					Cancel
				</v-btn>
				<v-btn :loading="exportReportLoading" color="primary text-none" @click="exportReport()">
					Export
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>
